import {
  OAuthCancelledError,
  OAuthUnavailableError,
  type ProviderCredential,
} from './oauthTypes'

/**
 * Apple Sign In on web via Apple's JS SDK.
 *
 * The Apple Services ID (not the iOS bundle ID) is used as the clientId; the
 * configured redirectURI must be allow-listed in the Apple Developer
 * console. Apple's JS SDK copies `init({ nonce })` into the ID token's nonce
 * claim verbatim (it does not hash). We send that same value to
 * oauth-exchange, which compares it to the claim.
 */

const APPLE_JS_SDK_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

type AppleAuthorizationResponse = {
  authorization: {
    code: string
    id_token: string
    state?: string
  }
  user?: {
    name?: { firstName?: string; lastName?: string }
  }
}

type AppleIdNamespace = {
  auth: {
    init: (config: {
      clientId: string
      nonce?: string
      redirectURI: string
      responseType?: string
      scope: string
      usePopup: boolean
    }) => void
    signIn: () => Promise<AppleAuthorizationResponse>
  }
}

declare global {
  interface Window {
    AppleID?: AppleIdNamespace
  }
}

let scriptLoadPromise: Promise<void> | null = null

function getAppleWebRedirectUri(): string {
  return (
    (import.meta.env.VITE_APPLE_WEB_REDIRECT_URI as string | undefined) ?? ''
  )
}

function getAppleWebServiceId(): string {
  return (import.meta.env.VITE_APPLE_WEB_SERVICE_ID as string | undefined) ?? ''
}

function loadAppleJsSdk(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new OAuthUnavailableError('Apple Sign In requires a browser environment'),
    )
  }
  if (window.AppleID) return Promise.resolve()
  if (scriptLoadPromise) return scriptLoadPromise

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${APPLE_JS_SDK_SRC}"]`,
    ) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Apple JS SDK')),
      )
      return
    }
    const script = document.createElement('script')
    script.src = APPLE_JS_SDK_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () =>
      reject(new Error('Failed to load Apple JS SDK')),
    )
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

function generateRandomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

export async function isAppleSignInAvailable(): Promise<boolean> {
  return Boolean(getAppleWebServiceId()) && Boolean(getAppleWebRedirectUri())
}

export async function signInWithApple(): Promise<ProviderCredential> {
  const appleWebRedirectUri = getAppleWebRedirectUri()
  const appleWebServiceId = getAppleWebServiceId()

  if (!appleWebServiceId || !appleWebRedirectUri) {
    throw new OAuthUnavailableError(
      'Apple Sign In is not configured for this funnel',
    )
  }
  await loadAppleJsSdk()
  if (!window.AppleID) {
    throw new OAuthUnavailableError('Apple JS SDK failed to initialise')
  }

  const rawNonce = generateRandomString(32)

  // `code id_token` makes Apple include the ID token in the popup response;
  // the default (`code`) would force a server-side code exchange, which
  // requires a .p8 private key we deliberately don't issue.
  window.AppleID.auth.init({
    clientId: appleWebServiceId,
    nonce: rawNonce,
    redirectURI: appleWebRedirectUri,
    responseType: 'code id_token',
    scope: 'name email',
    usePopup: true,
  })

  try {
    const response = await window.AppleID.auth.signIn()
    const idToken = response.authorization?.id_token
    if (!idToken) {
      throw new Error('Apple Sign In did not return an identity token')
    }
    const name = response.user?.name
    const displayName = name
      ? [name.firstName, name.lastName]
          .map((part) => part?.trim())
          .filter((part): part is string => Boolean(part && part.length > 0))
          .join(' ') || undefined
      : undefined
    return { displayName, idToken, nonce: rawNonce }
  } catch (error) {
    if (isUserCancellation(error)) {
      throw new OAuthCancelledError()
    }
    throw error
  }
}

function isUserCancellation(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const errorObject = error as { error?: string }
  return errorObject.error === 'popup_closed_by_user'
}
